/**
 * 公告栏 - 未读数管理
 */
import { getUnreadNoticeCount } from '@/api/safetyPrevention/noticeBoard'
import store from '@/store'

const noticeBoard = {
  namespaced: true,
  state: {
    // 各类型公告未读数
    promotionUnread: 0,      // 宣传公告未读数
    meetingUnread: 0,        // 会议公告未读数
    activityUnread: 0,       // 活动公告未读数
    totalUnread: 0,          // 总未读数
    pendingDeviceCount: 0,   // 待执行设备数量
    pendingRectificationCount: 0, // 整改任务待执行数量
    pendingConfirmationCount: 0, // 整改任务待确认数量
    samplingPendingRectificationCount: 0, // 抽检整改任务待执行数量
    samplingPendingConfirmationCount: 0, // 抽检整改任务待确认数量
    samplingPendingRectificationCountInside: 0, // 系统内抽检整改任务待执行数量
    samplingPendingRectificationCountOutside: 0, // 系统外抽检整改任务待执行数量
    samplingPendingConfirmationCountInside: 0, // 系统内抽检整改任务待确认数量
    samplingPendingConfirmationCountOutside: 0, // 系统外抽检整改任务待确认数量
    loading: false,
    notificationCallback: null,  // 通知回调函数
    lastFetchTime: 0  // 上次获取未读数的时间戳
  },

  getters: {
    promotionUnread: state => state.promotionUnread,
    meetingUnread: state => state.meetingUnread,
    activityUnread: state => state.activityUnread,
    totalUnread: state => state.totalUnread,
    pendingDeviceCount: state => state.pendingDeviceCount,
    pendingRectificationCount: state => state.pendingRectificationCount,
    pendingConfirmationCount: state => state.pendingConfirmationCount,
    samplingPendingRectificationCount: state => state.samplingPendingRectificationCount,
    samplingPendingConfirmationCount: state => state.samplingPendingConfirmationCount,
    samplingPendingRectificationCountInside: state => state.samplingPendingRectificationCountInside,
    samplingPendingRectificationCountOutside: state => state.samplingPendingRectificationCountOutside,
    samplingPendingConfirmationCountInside: state => state.samplingPendingConfirmationCountInside,
    samplingPendingConfirmationCountOutside: state => state.samplingPendingConfirmationCountOutside,
    loading: state => state.loading,
    // 检查用户是否有权限访问指定菜单
    hasMenuPermission: () => (menuPath) => {
      const routers = store.state.permission.routers
      if (!routers || routers.length === 0) {
        return false
      }
      
      // 递归检查路由是否存在且用户有权限
      function checkPermission(routes, path) {
        for (const route of routes) {
          // 构建完整路径
          let fullPath = route.path
          if (route.children && route.children.length > 0) {
            // 对于有子路由的情况，检查子路由
            for (const child of route.children) {
              const childPath = fullPath + '/' + child.path
              if (childPath === path) {
                return true
              }
              // 继续检查子路由的子路由
              if (child.children && child.children.length > 0) {
                for (const grandChild of child.children) {
                  const grandChildPath = childPath + '/' + grandChild.path
                  if (grandChildPath === path) {
                    return true
                  }
                }
              }
            }
          } else {
            // 对于没有子路由的情况，直接比较路径
            if (fullPath === path) {
              return true
            }
          }
        }
        return false
      }
      
      // 首先检查精确匹配
      let result = checkPermission(routers, menuPath)
      
      // 如果精确匹配失败，尝试模糊匹配
      if (!result) {
        // 对于一些特殊的路径，尝试匹配其父路径
        if (menuPath.includes('/noticeBoard/noticeBoard')) {
          // 检查公告栏菜单权限
          result = checkPermission(routers, '/safetyPrevention/noticeBoard')
        } else if (menuPath.includes('/noticeView/noticeView')) {
          // 检查公告查看菜单权限
          result = checkPermission(routers, '/safetyPrevention/noticeBoard/noticeView')
        } else if (menuPath.includes('/riskDeviceInspection/riskDeviceInspection')) {
          // 检查风险设备检验菜单权限
          result = checkPermission(routers, '/safetyPrevention/riskDeviceInspection')
        } else if (menuPath.includes('/riskInspectionMan/riskInspectionMan')) {
          // 检查风险排查管理菜单权限
          result = checkPermission(routers, '/safetyPrevention/riskInspectionMan')
        } else if (menuPath.includes('/riskGovernanceMan/riskGovernanceMan')) {
          // 检查风险治理管理菜单权限
          result = checkPermission(routers, '/safetyPrevention/riskGovernanceMan')
        }
      }
      
      return result
    }
  },
  
  mutations: {
    SET_PROMOTION_UNREAD(state, count) {
      state.promotionUnread = count
    },
    SET_MEETING_UNREAD(state, count) {
      state.meetingUnread = count
    },
    SET_ACTIVITY_UNREAD(state, count) {
      state.activityUnread = count
    },
    SET_TOTAL_UNREAD(state, count) {
      state.totalUnread = count
    },
    SET_PENDING_DEVICE_COUNT(state, count) {
      state.pendingDeviceCount = count
    },
    SET_PENDING_RECTIFICATION_COUNT(state, count) {
      state.pendingRectificationCount = count
    },
    SET_PENDING_CONFIRMATION_COUNT(state, count) {
      state.pendingConfirmationCount = count
    },
    SET_SAMPLING_PENDING_RECTIFICATION_COUNT(state, count) {
      state.samplingPendingRectificationCount = count
    },
    SET_SAMPLING_PENDING_CONFIRMATION_COUNT(state, count) {
      state.samplingPendingConfirmationCount = count
    },
    SET_SAMPLING_PENDING_RECTIFICATION_COUNT_INSIDE(state, count) {
      state.samplingPendingRectificationCountInside = count
    },
    SET_SAMPLING_PENDING_RECTIFICATION_COUNT_OUTSIDE(state, count) {
      state.samplingPendingRectificationCountOutside = count
    },
    SET_SAMPLING_PENDING_CONFIRMATION_COUNT_INSIDE(state, count) {
      state.samplingPendingConfirmationCountInside = count
    },
    SET_SAMPLING_PENDING_CONFIRMATION_COUNT_OUTSIDE(state, count) {
      state.samplingPendingConfirmationCountOutside = count
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_NOTIFICATION_CALLBACK(state, callback) {
      state.notificationCallback = callback
    },
    SET_LAST_FETCH_TIME(state, time) {
      state.lastFetchTime = time
    }
  },
  
  actions: {
    /**
     * 获取未读公告数
     */
    async fetchUnreadCount({ commit, rootGetters, state }) {
      const now = Date.now()
      const THROTTLE_TIME = 3000 // 3秒节流时间

      if (state.lastFetchTime && now - state.lastFetchTime < THROTTLE_TIME) {
        console.log('fetchUnreadCount 节流中，跳过本次调用')
        return
      }

      commit('SET_LAST_FETCH_TIME', now)

      try {
        commit('SET_LOADING', true)
        const response = await getUnreadNoticeCount()
        const data = response.data || response

        // 接口返回的数据结构为：
        // {
        //   "宣传公告": 0,
        //   "待执行设备数量": 3,
        //   "总数": 5,
        //   "会议公告": 2,
        //   "活动公告": 3
        // }
        let promotionCount = 0
        let meetingCount = 0
        let activityCount = 0
        let total = 0
        let pendingDeviceCount = 0
        let pendingRectificationCount = 0
        let pendingConfirmationCount = 0
        let samplingPendingRectificationCount = 0
        let samplingPendingConfirmationCount = 0
        let samplingPendingRectificationCountInside = 0
        let samplingPendingRectificationCountOutside = 0
        let samplingPendingConfirmationCountInside = 0
        let samplingPendingConfirmationCountOutside = 0

        if (typeof data === 'object' && data !== null) {
          // 宣传公告 - 检查对应菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/noticeBoard/noticeView')) {
            promotionCount = parseInt(data['宣传公告']) || 0
          }
          // 会议公告 - 检查对应菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/noticeBoard/noticeView')) {
            meetingCount = parseInt(data['会议公告']) || 0
          }
          // 活动公告 - 检查对应菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/noticeBoard/noticeView')) {
            activityCount = parseInt(data['活动公告']) || 0
          }
          // 总未读数 - 检查公告栏菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/noticeBoard/noticeBoard')) {
            total = parseInt(data['总数']) || 0
          }
          // 待执行设备数量 - 检查风险设备检验菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/riskDeviceInspection/riskDeviceInspection')) {
            pendingDeviceCount = parseInt(data['待执行设备数量']) || 0
          }
          // 整改任务待执行数量 - 检查排查任务整改菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/riskInspectionMan/rectificationTask')) {
            pendingRectificationCount = parseInt(data['整改任务待执行数量']) || 0
          }
          // 整改任务待确认数量 - 检查整改监管菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/riskInspectionMan/rectificationSupervision')) {
            pendingConfirmationCount = parseInt(data['整改任务待确认数量']) || 0
          }
          // 抽检整改任务待执行数量 - 检查抽检任务整改菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/riskGovernanceMan/samplingRectification')) {
            samplingPendingRectificationCount = parseInt(data['抽检整改任务待执行数量']) || 0
          }
          // 抽检整改任务待确认数量 - 检查整改监管菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/riskGovernanceMan/samplingSupervision')) {
            samplingPendingConfirmationCount = parseInt(data['抽检整改任务待确认数量']) || 0
          }
          // 系统内抽检整改任务待执行数量 - 检查抽检任务整改菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/riskGovernanceMan/samplingRectification')) {
            samplingPendingRectificationCountInside = parseInt(data['系统内抽检整改任务待执行数量']) || 0
          }
          // 系统外抽检整改任务待执行数量 - 检查抽检任务整改菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/riskGovernanceMan/samplingRectification')) {
            samplingPendingRectificationCountOutside = parseInt(data['系统外抽检整改任务待管理数量']) || 0
          }
          // 系统内抽检整改任务待确认数量 - 检查整改监管菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/riskGovernanceMan/samplingSupervision')) {
            samplingPendingConfirmationCountInside = parseInt(data['系统内抽检整改任务待确认数量']) || 0
          }
          // 系统外抽检整改任务待确认数量 - 检查整改监管菜单权限
          if (rootGetters['noticeBoard/hasMenuPermission']('/safetyPrevention/riskGovernanceMan/samplingSupervision')) {
            samplingPendingConfirmationCountOutside = parseInt(data['系统外抽检整改任务待确认数量']) || 0
          }
        }

        commit('SET_PROMOTION_UNREAD', promotionCount)
        commit('SET_MEETING_UNREAD', meetingCount)
        commit('SET_ACTIVITY_UNREAD', activityCount)
        commit('SET_TOTAL_UNREAD', total)
        commit('SET_PENDING_DEVICE_COUNT', pendingDeviceCount)
        commit('SET_PENDING_RECTIFICATION_COUNT', pendingRectificationCount)
        commit('SET_PENDING_CONFIRMATION_COUNT', pendingConfirmationCount)
        commit('SET_SAMPLING_PENDING_RECTIFICATION_COUNT', samplingPendingRectificationCount)
        commit('SET_SAMPLING_PENDING_CONFIRMATION_COUNT', samplingPendingConfirmationCount)
        commit('SET_SAMPLING_PENDING_RECTIFICATION_COUNT_INSIDE', samplingPendingRectificationCountInside)
        commit('SET_SAMPLING_PENDING_RECTIFICATION_COUNT_OUTSIDE', samplingPendingRectificationCountOutside)
        commit('SET_SAMPLING_PENDING_CONFIRMATION_COUNT_INSIDE', samplingPendingConfirmationCountInside)
        commit('SET_SAMPLING_PENDING_CONFIRMATION_COUNT_OUTSIDE', samplingPendingConfirmationCountOutside)

        commit('SET_LOADING', false)
      } catch (error) {
        console.error('获取未读公告数失败:', error)
        commit('SET_LOADING', false)
      }
    },
    
    /**
     * 重置未读数
     */
    resetUnreadCount({ commit }) {
      commit('SET_PROMOTION_UNREAD', 0)
      commit('SET_MEETING_UNREAD', 0)
      commit('SET_ACTIVITY_UNREAD', 0)
      commit('SET_TOTAL_UNREAD', 0)
      commit('SET_PENDING_DEVICE_COUNT', 0)
      commit('SET_PENDING_RECTIFICATION_COUNT', 0)
      commit('SET_PENDING_CONFIRMATION_COUNT', 0)
      commit('SET_SAMPLING_PENDING_RECTIFICATION_COUNT', 0)
      commit('SET_SAMPLING_PENDING_CONFIRMATION_COUNT', 0)
      commit('SET_SAMPLING_PENDING_RECTIFICATION_COUNT_INSIDE', 0)
      commit('SET_SAMPLING_PENDING_RECTIFICATION_COUNT_OUTSIDE', 0)
      commit('SET_SAMPLING_PENDING_CONFIRMATION_COUNT_INSIDE', 0)
      commit('SET_SAMPLING_PENDING_CONFIRMATION_COUNT_OUTSIDE', 0)
    },

    /**
     * 设置通知回调函数
     */
    setNotificationCallback({ commit }, callback) {
      commit('SET_NOTIFICATION_CALLBACK', callback)
    },

    /**
     * 更新未读数并触发通知
     */
    async updateUnreadCountAndNotify({ dispatch, state }) {
      await dispatch('fetchUnreadCount')
      // 触发通知回调
      if (state.notificationCallback && typeof state.notificationCallback === 'function') {
        state.notificationCallback()
      }
    }
  }
}

export default noticeBoard

